<template>
  <div class="view-product-offers">
    <h5 class="text-semibold mb-5">Precificação e ofertas</h5>
    <div class="row mb-7">
      <div class="col-lg-6">
        <info-card
          title="Preço base"
          icon="ph ph-currency-circle-dollar"
          :value="formatMoney(product.price)"
        />
      </div>
      <div class="col-lg-6 mt-4 mt-md-0">
        <info-card
          title="Garantia do produto"
          prefix=""
          icon="ph ph-shield-check"
          :value="`${product.warranty} dia${product.warranty == 1 ? '' : 's'}`"
        />
      </div>
    </div>

    <snow-switch
      class="mb-3"
      v-model="smartInstallment"
      label="Ativar recuperação de vendas neste produto"
    />

    <snow-data-table-header
      @add="newOffer"
      @searching="searching"
      :filters="false"
      :date-picker="false"
    />

    <div v-if="loading" class="text-center">
      <b-spinner variant="primary" />
    </div>

    <div v-else>
      <snow-data-table :columns="columns" :data="offerOptions" class="mt-3" />
    </div>

    <div class="d-flex justify-content-end mt-5">
      <snow-paginator
        v-show="!loading"
        v-model="currentPage"
        :pagination="pagination"
        @change-page="dispatch"
      />
    </div>
  </div>
</template>

<script>
import { clearObject, formatMoney } from "@/functions/helpers";
import { defaultOffer } from "@/functions/product";
import ProductActionsMixin from "@/mixins/productActions.js";
import copy from "copy-text-to-clipboard";
import SnowSwitch from "../../../components/snow-ui/SnowSwitch.vue";
import { mapState } from "vuex";

export default {
  mixins: [ProductActionsMixin],
  components: {
    SnowSwitch
  },
  data() {
    return {
      search: "",
      loading: false,
      currentPage: 1
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    ...mapState("offer", ["offers", "pagination"]),
    offerOptions() {
      return [defaultOffer(this.product), ...this.offers];
    },
    isActive() {
      return this.product.status === "active";
    },
    smartInstallment: {
      get() {
        return this.product.smart_installment;
      },
      set(value) {
        this.$store
          .dispatch("product/put", {
            id: this.$route.params.id,
            smart_installment: value
          })
          .then(() => {
            this.$message.success(
              `Recuperação de vendas ${
                value ? "ativada" : "desativada"
              } com sucesso!`
            );
          })
          .catch(() => {
            this.$message.error("Erro ao atualizar recuperação de vendas.");
          });
      }
    },
    columns() {
      return [
        {
          name: "Nome",
          field: "name"
        },
        {
          name: "Código",
          field: "code"
        },
        {
          name: "Real Brasileiro (R$)",
          field: "price",
          transform: v => `R$ ${formatMoney(v)}`
        },
        {
          name: "Dólar Americano (US$)",
          field: "price_usd",
          transform: v => (parseFloat(v) ? `$ ${formatMoney(v)}` : "-")
        },
        {
          name: "Euro (€)",
          field: "price_eur",
          transform: v => (parseFloat(v) ? `€ ${formatMoney(v)}` : "-")
        },
        {
          name: "SCK",
          field: "sck"
        },
        {
          name: "Status",
          field: "status",
          transform: this.formatStatus,
          color: v =>
            ({
              draft: "alert",
              active: "green",
              inactive: "negative"
            }[v]),
          type: "tag"
        },
        {
          name: "",
          type: "menu",
          options: this.itemActions
        }
      ];
    }
  },
  methods: {
    formatStatus(val) {
      const options = {
        draft: "Pendente",
        active: "Ativo",
        inactive: "Inativo"
      };
      return options[val] || val;
    },
    changeOfferStatus(item) {
      this.$store
        .dispatch("offer/put", {
          id: item.id,
          status: item.status === "active" ? "inactive" : "active"
        })
        .then(() => {
          this.$message.success("Status da oferta alterado com sucesso.");
          this.dispatch();
        })
        .catch(() => {
          this.$message.error("Erro ao alterar status da oferta.");
        });
    },
    makeMain(item) {
      this.$store
        .dispatch("product/put", {
          id: this.product.id,
          price: item.price
        })
        .then(() => {
          this.$message.success(
            `Agora o preço base é de R$ ${formatMoney(item.price)}.`
          );
        })
        .catch(() => {
          this.$message.error("Erro ao atualizar oferta principal");
        });
    },
    formatMoney(val) {
      return formatMoney(val);
    },
    newOffer() {
      this.$router.push({
        name: "product-prices-new",
        params: { id: this.$route.params.id }
      });
    },
    itemActions(item) {
      const actions = [];

      actions.push({
        label: "Editar",
        click: this.edit
      });

      actions.push({
        label: "Copiar link da oferta",
        click: this.copy
      });

      if (item.id !== -1) {
        actions.push({
          label: "Tornar preço base",
          dialog: {
            text:
              "Tem certeza que deseja tornar esta oferta como a principal? Após clicar em confirmar, os clientes que acessarem o link de checkout principal do seu produto encontrarão essa oferta.",
            buttonText: "Prosseguir",
            secondaryButtonText: "Cancelar",
            action: this.makeMain,
            icon: "fa-light fa-circle-info"
          }
        });

        actions.push({
          label: "Duplicar oferta",
          click: this.duplicate
        });

        if (item.id !== -1) {
          actions.push({
            label: `${
              item.status === "active" ? "Desativar" : "Ativar"
            } oferta`,
            class: item.status === "active" ? "text-danger" : "",
            click: this.changeOfferStatus
          });
        }

        actions.push({
          label: "Excluir oferta",
          class: "text-danger",
          variant: "danger",
          dialog: {
            text:
              "Tem certeza que deseja excluir esta oferta? Seus clientes não poderão mais encontrar e comprar através desta oferta.",
            variant: "negative",
            icon: "ph ph-trash",
            action: i => this.deleteOffer(i.id),
            secondaryButtonText: "Cancelar",
            buttonText: "Excluir oferta"
          }
        });
      }
      return actions;
    },
    edit(item) {
      if (item.is_main) {
        this.$router.push({ name: "product-prices-main" });
      } else {
        this.$router.push({
          name: "product-prices-edit",
          params: { id: this.$route.params.id, offer_id: item.id }
        });
      }
    },
    copy(item) {
      copy(item.url);
      this.$message.success("Link copiado");
    },
    duplicate(item) {
      this.$store
        .dispatch("offer/duplicate", item)
        .then(() => {
          this.dispatch();
          this.$message.success("Oferta duplicado.");
        })
        .catch(() => this.$message.error("Erro ao duplicar oferta."));
    },
    deleteOffer(id) {
      this.$store
        .dispatch("offer/delete", id)
        .then(() => {
          this.$message.success("Oferta deletada");
          this.dispatch();
        })
        .catch(() => this.$message.error("Erro ao deletar oferta"));
    },
    searching(search) {
      this.search = search;
      this.dispatch();
    },
    dispatch(offset = 0) {
      this.loading = true;

      const params = {
        product_id: this.$route.params.id,
        search: this.search,
        offset
      };

      this.$store
        .dispatch("offer/get", clearObject(params))
        .catch(() => {
          this.$message.error("Não foi possível carregar as ofertas");
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  mounted() {
    this.dispatch();
  }
};
</script>
